import type { CommunityWorkflowConfiguration, WorkflowType, ContentTypeTps } from '@communities/content-management/domain';
import { useCommunityWorkflows, useUpsertCommunityWorkflow } from '@communities/content-management/hooks';
import { ErrorBanner } from '@core/components';
import { TextInput, LoadingSpinner, Dialog } from '@soteria/design-system-ve';
import { useState, useEffect } from 'react';

import * as styles from './ContentTypeRateLimitDialog.css';

interface ContentTypeRateLimitDialogProps {
  communityId: string;
  workflowType: WorkflowType;
  isOpen?: boolean;
  onClose: () => void;
}

/**
 * Dialog component for editing per-content-type rate limiting
 * Uses simple inputs - empty values mean "not configured"
 */
export function ContentTypeRateLimitDialog({ communityId, workflowType, isOpen = false, onClose }: ContentTypeRateLimitDialogProps) {
  const { workflows } = useCommunityWorkflows(communityId, workflowType);
  const { upsertWorkflow, loading: isSubmitting, errors, resetErrors } = useUpsertCommunityWorkflow();
  const [audioTps, setAudioTps] = useState<string>('');
  const [imageTps, setImageTps] = useState<string>('');
  const [textTps, setTextTps] = useState<string>('');

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (isOpen && workflows) {
      const contentTypeTps = workflows?.apiSettings?.contentTypeTps;

      setAudioTps(contentTypeTps?.audio?.toString() || '');
      setImageTps(contentTypeTps?.image?.toString() || '');
      setTextTps(contentTypeTps?.text?.toString() || '');
      resetErrors();
    }
  }, [isOpen, resetErrors, workflows]);

  // eslint-disable-next-line complexity
  const handleSubmit = async () => {
    resetErrors();

    // Build contentTypeTps from entered values
    const contentTypeTps: ContentTypeTps = {};

    if (audioTps) contentTypeTps.audio = parseInt(audioTps, 10);
    if (imageTps) contentTypeTps.image = parseInt(imageTps, 10);
    if (textTps) contentTypeTps.text = parseInt(textTps, 10);

    const hasContentTypeTps = Object.keys(contentTypeTps).length > 0;

    const updatedWorkflow: CommunityWorkflowConfiguration = {
      type: workflowType,
      topicActions: workflows?.topicActions || [],
      apiSettings: {
        accessClients: workflows?.apiSettings?.accessClients || [],
        monthlyBudget: workflows?.apiSettings?.monthlyBudget,
        maxTps: workflows?.apiSettings?.maxTps,
        ...(hasContentTypeTps && { contentTypeTps }),
      },
    };

    const success = await upsertWorkflow(communityId, workflowType, updatedWorkflow);

    if (success) onClose();
  };

  const dialogTitle = `${workflowType.charAt(0).toUpperCase() + workflowType.slice(1)} Per-Content-Type Rate Limits`;
  const dialogDescription = `Configure separate rate limits for different content types. Leave empty to use default rate limit.`;

  return (
    <Dialog
      description={dialogDescription}
      isOpen={isOpen}
      primaryAction={{
        label: isSubmitting ? <LoadingSpinner /> : 'Update Settings',
        onClick: handleSubmit,
        disabled: isSubmitting,
      }}
      size="md"
      title={dialogTitle}
      onClose={onClose}
    >
      <div className={styles.container}>
        {/* Error Banner */}
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error updating the rate limit settings:" />}

        {/* Audio TPS */}
        <TextInput
          disabled={isSubmitting}
          helperText="Leave empty to use default rate limit"
          label="Audio TPS"
          min="1"
          placeholder="Optional"
          type="number"
          value={audioTps}
          onChange={setAudioTps}
        />

        {/* Image TPS */}
        <TextInput
          disabled={isSubmitting}
          helperText="Leave empty to use default rate limit"
          label="Image TPS"
          min="1"
          placeholder="Optional"
          type="number"
          value={imageTps}
          onChange={setImageTps}
        />

        {/* Text TPS */}
        <TextInput
          disabled={isSubmitting}
          helperText="Leave empty to use default rate limit"
          label="Text TPS"
          min="1"
          placeholder="Optional"
          type="number"
          value={textTps}
          onChange={setTextTps}
        />
      </div>
    </Dialog>
  );
}
